import { RenderManager } from "./three/RenderManager";
import { ControllerManager } from "./three/ControllerManager";
import { SceneManager } from "./three/SceneManager";
import { LightsManager } from "./lighting/LightsManager";

import Scene from "../components/scene/Scene";
import * as THREE from "three";
import { logger } from "@/utils/logger";

import { Ao } from "./ao/Ao";
//import ColorEngine from "./color-engine/ColorEngine";
import { loader } from "@/utils/loader";
import { ColorEngine } from "./color-engine/ColorEngine";

export interface ServiceContainerConfig {
  width: number;
  height: number;
  aoEnabled: boolean;
  isMobile: boolean;
  ssaa?: boolean;
}

export interface InitializationStatus {
  isInitialized: boolean;
  errors: string[];
  warnings: string[]; // New: track warnings separately from errors
}

export class ServiceContainer {
  private _isInitialized = false;
  private _initializationStatus: InitializationStatus = {
    isInitialized: false,
    errors: [],
    warnings: []
  };

  // Static instance accessors - these return the singleton instances
  get sceneManager(): SceneManager {
    return SceneManager.getInstance();
  }

  get renderManager(): RenderManager {
    return RenderManager.getInstance();
  }

  get controllerManager(): ControllerManager {
    return ControllerManager.getInstance();
  }

  get lightsManager(): LightsManager {
    return LightsManager.getInstance();
  }

  get ao(): Ao {
    return Ao.getInstance();
  }

  get colorEngine(): ColorEngine {
    return ColorEngine.getInstance();
  }

  raycaster: THREE.Raycaster;

  constructor() {
    this.raycaster = new THREE.Raycaster();
  }

  get initializationStatus(): InitializationStatus {
    return { ...this._initializationStatus };
  }

  // Service getters

  get lightsManagerInstance(): LightsManager {
    return this.lightsManager;
  }

  async initialize(
    config: ServiceContainerConfig
  ): Promise<InitializationStatus> {
    if (this._isInitialized) {
      logger.warn(
        "ServiceContainer is already initialized",
        null,
        "ServiceContainer"
      );
      return this._initializationStatus;
    }

    try {
      await this.initializeCoreServices(config);

      await this.initializeAO(config);
      //await this.initializeColorEngine();

      this._isInitialized = true;
      this._initializationStatus.isInitialized = true;

      return this._initializationStatus;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown initialization error";
      this._initializationStatus.errors.push(errorMessage);
      logger.error(
        "ServiceContainer initialization failed",
        error,
        "ServiceContainer"
      );

      return this._initializationStatus;
    }
  }

  private async initializeCoreServices(
    config: ServiceContainerConfig
  ): Promise<void> {
    try {
      // Initialize scene first
      this.initializeDefaultScene();

      // Initialize renderer and set it in scene manager
      await this.initializeRenderer(config);

      // Initialize cameras (needs renderer)
      this.initializeCameras(config.width, config.height);

      // Initialize controller manager (needs camera and renderer)
      this.initializeControllerManager();

      // ThemeManager initialization removed - using themeUtils directly

      // Initialize Loader with dependencies
      this.initializeLoader();
    } catch (error) {
      logger.error(
        "Core services initialization failed",
        error,
        "ServiceContainer"
      );
      throw error;
    }
  }

  private async initializeRenderer(
    config: ServiceContainerConfig
  ): Promise<void> {
    try {
      await this.renderManager.initRenderer(config.width, config.height);
      this.sceneManager.renderer = this.renderManager.getRenderer();

      // Initialize LightsManager with the renderer
      if (this.sceneManager.renderer) {
        this.lightsManager.initialize(this.sceneManager.renderer);
      } else {
        throw new Error("Failed to get renderer from RenderManager");
      }
    } catch (error) {
      logger.error("Renderer initialization failed", error, "ServiceContainer");
      throw error;
    }
  }

  // initializeThemeManager removed - using themeUtils directly

  private initializeCameras(width: number, height: number): void {
    const FOV = 46;
    const NEAR_CLIP = 3;
    const FAR_CLIP = 1000;
    const SCREENSHOT_NEAR_CLIP = 0.01;
    const SCREENSHOT_FAR_CLIP = 1000;

    // Main perspective camera
    this.sceneManager.perspectiveCamera = new THREE.PerspectiveCamera(
      FOV,
      width / height,
      NEAR_CLIP,
      FAR_CLIP
    );
    // Screenshot camera
    this.sceneManager.screenshotCamera = new THREE.PerspectiveCamera(
      FOV,
      width / height,
      SCREENSHOT_NEAR_CLIP,
      SCREENSHOT_FAR_CLIP
    );
    this.sceneManager.screenshotCamera.visible = false;
    this.sceneManager.camera = this.sceneManager.perspectiveCamera;
  }

  private initializeControllerManager(): void {
    this.controllerManager.initialize(
      this.sceneManager.perspectiveCamera!,
      this.sceneManager.renderer!
    );
  }

  private initializeDefaultScene(): void {
    const scene = new Scene();
    scene.name = "mainScene";
    this.sceneManager.scene = scene;
  }

  private initializeAO(config: ServiceContainerConfig): void {
    this.ao.setProperties(
      config.aoEnabled,
      config.isMobile,
      config.ssaa || true,
      this.sceneManager,
      this.renderManager.getEffectComposer(),
      this.controllerManager
    );
    this.renderManager.setRenderingPipeline(this.ao);
  }

  private initializeLoader(): void {
    // Set dependencies for the singleton Loader instance
    loader.setDependencies(this.ao);
  }

  reset(): void {
    try {
      if (this.sceneManager.renderer) {
        this.sceneManager.renderer.dispose();
        this.sceneManager.renderer = null;
      }

      if (this.sceneManager.scene) {
        this.sceneManager.scene.clear();
        this.sceneManager.scene = null;
      }

      this.controllerManager.reset();

      // Reset Loader dependencies
      loader.setDependencies(undefined);
      this._isInitialized = false;
      this._initializationStatus = {
        isInitialized: false,
        errors: [],
        warnings: []
      };
    } catch (error) {
      logger.error(
        "Error during ServiceContainer reset",
        error,
        "ServiceContainer"
      );
    }
  }

  getServiceStatus(): Record<string, boolean> {
    return {
      renderer: !!this.sceneManager.renderer,
      camera: !!this.sceneManager.perspectiveCamera,
      scene: !!this.sceneManager.scene,
      controllerManager: !!this.controllerManager,
      lightsManager: this.lightsManager.getInitialized(),
      ao: !!this.ao,
      colorEngine: !!this.colorEngine,
      loader: loader.hasDependencies()
    };
  }
}
